import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import React, { useRef, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedOrCoreError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import RendererWithHorizontalPadding from 'shared/renderer/RendererWithHorizontalPadding';
import { VXLGroupId, VXLNode } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

import useBorrowingPowerScreenData from './useBorrowingPowerScreenData/useBorrowingPowerScreenData';

const endsWithListGroup = (nodes: VXLNode[]) => {
  if (!nodes.length) {
    return false;
  }
  const node = nodes[nodes.length - 1];
  if ('name' in node && node.name === VXLGroupId.LIST_ITEM_GROUP) {
    return true;
  }
  return false;
};

const BorrowingPowerScreen = () => {
  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const [bottomSheetChildren, setBottomSheetChildren] = useState<VXLNode[]>([]);
  const { bottom } = useSafeAreaInsets();

  const showBottomSheet = (content: VXLNode[]) => {
    setBottomSheetChildren(content);
    bottomSheetRef.current?.present();
  };

  const dismissBottomSheet = () => {
    setBottomSheetChildren([]);
    bottomSheetRef.current?.dismiss();
  };

  const { retryButtonTitle, cancelButtonTitle } = useDefaultErrorStrings();
  const router = useRouter();
  const { content, fetching, error, refetch } = useBorrowingPowerScreenData({
    showBottomSheet,
    dismissBottomSheet,
  });
  const formattedError = useFormattedCombinedOrCoreError(error);

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: '',
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScrollView contentContainerStyle={{ paddingBottom: bottom }}>
        {content?.sections.map((section, index) => {
          // concat all child keys to create a unique key for the section
          // this will be long but only n * keylen where n is ~5 so not worth optimizing over.
          const allKeys = section.map((i) => i.id).join('-');
          // the list item group has no bottom padding when it is the last item
          // account for that exception here if the section ends with it
          const bottomPaddingStyle = endsWithListGroup(section)
            ? {}
            : styles.bottomPaddingStyle;

          const isLast = !(index < content.sections.length - 1);
          return (
            <React.Fragment key={`fragment-${allKeys}-${isLast}`}>
              <View style={{ ...styles.section, ...bottomPaddingStyle }}>
                {section.map((element) => (
                  <RendererWithHorizontalPadding
                    key={element.id}
                    toRender={element}
                    paddingHorizontal="m"
                  />
                ))}
              </View>
              {isLast ? null : <View style={styles.sectionSeparator} />}
            </React.Fragment>
          );
        })}
      </ScrollView>
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          formattedError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={formattedError.title}
              messageBody={formattedError.message}
              primaryButton={{
                title: retryButtonTitle,
                onPress: () => refetch({ requestPolicy: 'network-only' }),
              }}
              secondaryButton={{
                title: cancelButtonTitle,
                onPress: () => {
                  if (router.canGoBack()) {
                    router.back();
                  }
                },
              }}
            />
          ) : null
        }
      />
      <BottomSheet ref={bottomSheetRef}>
        <View style={[styles.bottomSheetContainer, { paddingBottom: bottom }]}>
          {bottomSheetChildren.map((element) => (
            <RendererWithHorizontalPadding
              key={element.id}
              toRender={element}
              paddingHorizontal="m"
            />
          ))}
        </View>
      </BottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  bottomPaddingStyle: {
    paddingBottom: theme.spacing.m,
  },
  bottomSheetContainer: {
    paddingBottom: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexDirection: 'column',
    flexGrow: 1,
  },
  section: {
    gap: theme.spacing.s,
    paddingTop: theme.spacing.m,
  },
  sectionSeparator: {
    backgroundColor: theme.color.background.tertiary,
    height: theme.spacing.s,
  },
});

export default BorrowingPowerScreen;
